import { PayNowStatus, PolicyStatus, Product } from '@acorn-portal/shared/src/services/api';
import { IPayNow } from '@acorn-portal/shared/src/store/paynow.slice';

/**
 * Set of policy statuses where opt-out is NOT allowed
 */
export const NON_OPT_OUT_STATUSES: Set<PolicyStatus> = new Set([
	PolicyStatus.RenewalTakenUp,
	PolicyStatus.Cancelled,
	PolicyStatus.Expired,
]);

/**
 * Determines if a policy can opt out based on its status
 * @param policyStatus The status of the policy
 * @returns boolean indicating if the policy can opt out
 */
export const canOptOut = (policyStatus: PolicyStatus): boolean => {
	return !NON_OPT_OUT_STATUSES.has(policyStatus);
};

/**
 * Checks if short-term policy changes are allowed
 */
export const isShortTermChangeDisabled = (isShortTermPolicy: boolean, product: Product): boolean => {
	return isShortTermPolicy && product !== Product.TAXI;
};

/**
 * Checks if policy changes are disabled based on payment status
 */
export const isPaymentStatusChangeDisabled = (payNowDetails: IPayNow): boolean => {
	return (
		(payNowDetails?.missingPaymentFormDetails !== undefined &&
			payNowDetails?.details?.status === PayNowStatus.DirectDebitOverdue) ||
		payNowDetails?.details?.status === PayNowStatus.SplitPaymentOverdue ||
		payNowDetails?.details?.status === PayNowStatus.ConsolidatedPaymentSchedule
	);
};

/**
 * Policy statuses where changes are not allowed
 */
const NO_CHANGES_POLICY_STATUSES: Set<PolicyStatus> = new Set([
	PolicyStatus.Pending,
	PolicyStatus.RenewalTakenUp,
	PolicyStatus.DueForRenewal,
]);

/**
 * Determines if changes are disabled based on policy status
 */
export const isPolicyChangeDisabled = (policyStatus: PolicyStatus): boolean => {
	return NO_CHANGES_POLICY_STATUSES.has(policyStatus);
};

export interface MileageCardVisibilityParams {
	isTelematicsEnabled: boolean;
	summaryLoading: boolean;
	summaryError: string | null;
	milesDrivenThisTerm: number | undefined;
	driverMileageLoading: boolean;
	driverMileageError: string | null;
	actualMiles: number | undefined;
}

/**
 * Determines whether the mileage card should be shown
 */
export const shouldShowMileageCard = (params: MileageCardVisibilityParams): boolean => {
	const {
		isTelematicsEnabled,
		summaryLoading,
		summaryError,
		milesDrivenThisTerm,
		driverMileageLoading,
		driverMileageError,
		actualMiles,
	} = params;

	if (isTelematicsEnabled) {
		return !summaryLoading && !summaryError && !!milesDrivenThisTerm && milesDrivenThisTerm > 0;
	}

	return !driverMileageLoading && !driverMileageError && !!actualMiles && actualMiles > 0;
};
